import { Page } from "@shopify/polaris"
import React, { createContext, useContext, useEffect, useState } from "react"
import classNames from "classnames/bind"
import Loading from "components/widgets/Loading"
import { useTranslation } from "react-i18next"
import Message from "components/widgets/Dashboard/Message"
import { useAppContext } from "components/AppContext"
import {
  PILLAR_ENVIRONMENTAL,
  PILLAR_PROFIT,
  PILLAR_SOCIAL,
} from "utils/constants/pillars"
import ObjectiveNavigationTemplate from "views/templates/ObjectiveNavigationTemplate"
import withAuthRequired from "../auth/withAuthRequired"
import { useEntity } from "../../hooks/useAxios"
import useRequest from "../../hooks/useRequest"
import { IMPROVE_WEBSITES_PAGE } from "utils/constants/improve/pages"
import usePillars from "views/organisms/pillars/usePillars"
import RedirectIfDisabled from "views/molecules/RedirectIfDisabled"
import WebsiteRecommendationEntity from "services/entities/WebsiteRecommendationEntity"
import { useHistory, useParams } from "react-router-dom"
import AnalyzeGHGTemplate from "views/templates/AnalyzeGHGTemplate"
import WebsiteRecommendation from "components/widgets/WebsiteImprovePage/WebsiteRecommendation"
import { ErrorBoundary } from "@sentry/react"
import ImproveWebsiteErrorBoundary from "components/ErrorBoundary/ImproveWebsiteErrorBoundary"

export const ImproveContext = createContext()

export function useImproveContext() {
  return useContext(ImproveContext)
}

const ImproveWebsitesPage = () => {
  const { id } = useParams()
  const [contextFilter, setContextFilter] = useState("all")
  const { t } = useTranslation()
  const history = useHistory()

  const {
    currentPeriod,
    currentNode,
    isEnvScoreLoading,
    isCurrentNodeLoading,
    currentRootNode,
    isCurrentRootNodeLoading,
    parentOrganization,
  } = useAppContext()
  const recommendationEntity = useEntity(WebsiteRecommendationEntity)

  const { data: recommendations, isLoading: isRecommendationsLoading } =
    useRequest(() => recommendationEntity.index(), [currentNode], {
      condition: !!currentNode,
      getter: (data) => data,
    })

  const activeTabId =
    id ??
    (recommendations?.is_consolidated
      ? String(recommendations?.tabs?.[0]?.id)
      : undefined)

  const tabs = recommendations?.tabs?.map((tab) => {
    const tabId = String(tab.id)

    return {
      id: tab.id,
      name:
        tabId === "websites"
          ? t("improve:websites.consolidated_tab")
          : tab.title,
      url: recommendations?.is_consolidated
        ? "/improve/websites"
        : `/improve/websites/${tab.id}`,
      selected: tabId === String(activeTabId),
    }
  })

  const firstTabId = tabs?.[0]?.id

  const pillars = usePillars({
    pageName: recommendations?.is_consolidated
      ? "/improve/websites"
      : `/improve/websites/${firstTabId}`,
    [PILLAR_ENVIRONMENTAL]: { disabled: false, selected: true },
    [PILLAR_SOCIAL]: { disabled: true },
    [PILLAR_PROFIT]: { disabled: true },
  })

  useEffect(() => {
    if (isRecommendationsLoading || !recommendations) return

    if (!recommendations?.tabs?.length) {
      history.push("/improve/maturity")
      return
    }

    if (recommendations.is_consolidated) {
      if (id && id !== "websites") {
        history.push("/improve/websites")
      }
      return
    }

    if (!id || !recommendations.websites_audits[id]) {
      history.push(`/improve/websites/${recommendations.tabs[0].id}`)
    }
  }, [recommendations, isRecommendationsLoading, id])

  if (currentRootNode && !currentRootNode.has_children) {
    return <Message messageId="no_node_children" show />
  }

  const objectives = [
    {
      objectiveId: 13,
      selected: true,
      url: recommendations?.is_consolidated
        ? "/improve/websites"
        : `/improve/websites/${firstTabId}`,
    },
  ]

  const activeAudits = recommendations?.websites_audits?.[activeTabId]
  const failedAudits = activeAudits?.failed_audits || []
  const totalPages = activeAudits?.total_pages

  if (
    isRecommendationsLoading ||
    isEnvScoreLoading ||
    isCurrentNodeLoading ||
    isCurrentRootNodeLoading
  ) {
    return <Loading />
  }

  const availableContexts = ["eco_conception", "accessibility"].filter(
    (context) => failedAudits.some((audit) => audit.context === context)
  )

  // Only offer the filter when both types of recommendations are present.
  const showContextFilter = availableContexts.length > 1

  const filteredAudits =
    !showContextFilter || contextFilter === "all"
      ? failedAudits
      : failedAudits.filter((audit) => audit.context === contextFilter)

  const filterOptions = ["all", ...availableContexts]

  return (
    <ErrorBoundary fallback={<ImproveWebsiteErrorBoundary />}>
      <ObjectiveNavigationTemplate changeToDefault={false}>
        <RedirectIfDisabled pageName={`/${IMPROVE_WEBSITES_PAGE}`}>
          <Page fullWidth>
            <AnalyzeGHGTemplate
              needEnvScore={false}
              parentOrganization={parentOrganization}
              themeType={PILLAR_ENVIRONMENTAL}
              objectives={objectives}
              pillars={pillars}
              tabs={tabs}
              periodName={currentPeriod?.name}
              forceSize
            >
              <div
                className={`recommendationsLayout recommendationsLayout--environmental recommendationsLayout--website`}
              >
                <div className="recommendationsLayout__recommendations recommendationsLayout__recommendations--website">
                  {showContextFilter && (
                    <div className="recommendations__filter">
                      {filterOptions.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={classNames(
                            "recommendations__filterButton",
                            `recommendations__filterButton--${option}`,
                            {
                              "recommendations__filterButton--selected":
                                contextFilter === option,
                            }
                          )}
                          onClick={() => setContextFilter(option)}
                        >
                          {t(`improve:websites.filter.${option}`)}
                        </button>
                      ))}
                    </div>
                  )}
                  <div
                    className={`recommendations recommendations--environmental`}
                  >
                    <div className="recommendations__tableContainer">
                      <table className="recommendations__table recommendations__table--website">
                        <thead>
                          <tr>
                            <th className="recommendations__thText" scope="col">
                              {t(`improve:recommendations_table.header.impact`)}
                            </th>
                            <th className="recommendations__thText" scope="col">
                              {t(
                                `improve:recommendations_table.header.complexity`
                              )}
                            </th>
                            <th className="recommendations__thText" scope="col">
                              {t(
                                `improve:recommendations_table.header.recommendation`
                              )}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAudits.map((recommendation, index) => (
                            <WebsiteRecommendation
                              key={index}
                              recommendation={recommendation}
                              totalPages={totalPages}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </AnalyzeGHGTemplate>
          </Page>
        </RedirectIfDisabled>
      </ObjectiveNavigationTemplate>
    </ErrorBoundary>
  )
}

export default withAuthRequired(ImproveWebsitesPage)
